<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import Banner from 'dashboard/components/ui/Banner.vue';
import { LocalStorage } from 'shared/helpers/localStorage';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';

const DISMISS_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours

const store = useStore();
const { t, locale } = useI18n();
const router = useRouter();

const isDismissed = ref(false);

const accountId = computed(() => store.getters.getCurrentAccountId);
const currentAccount = computed(
  () => store.getters['accounts/getAccount'](Number(accountId.value)) || {}
);
// Estado por conta (vindo do show da conta); fallback no da instancia.
const vinctoSubscription = computed(
  () =>
    currentAccount.value.vincto_subscription ||
    store.getters['globalConfig/getFazerAiSubscription']
);
const isVinctoSubscriptionPastDue = computed(
  () => vinctoSubscription.value?.status === 'past_due'
);
const isVinctoSubscriptionTrialing = computed(
  () => vinctoSubscription.value?.status === 'trialing'
);
const isVinctoSubscriptionCanceling = computed(
  () =>
    vinctoSubscription.value?.status === 'active' &&
    Boolean(vinctoSubscription.value?.cancel_at_period_end)
);
const currentUser = computed(() => store.getters.getCurrentUser);

const isSuperAdmin = computed(() => currentUser.value?.type === 'SuperAdmin');
const isAccountAdmin = computed(
  () => store.getters.getCurrentRole === 'administrator'
);
const showActionButton = computed(
  () => isSuperAdmin.value || isAccountAdmin.value
);

const resolvedLocale = computed(() => {
  const currentLocale = locale.value || navigator.language || 'en';
  return currentLocale.replace('_', '-');
});

const formattedPeriodEnd = computed(() => {
  const periodEnd = vinctoSubscription.value?.current_period_end;
  if (!periodEnd) return '';
  const date = new Date(periodEnd * 1000);
  return new Intl.DateTimeFormat(resolvedLocale.value, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(date);
});

const daysUntilTrialEnd = computed(() => {
  const periodEnd = vinctoSubscription.value?.current_period_end;
  if (!periodEnd) return null;
  const endDate = new Date(periodEnd * 1000);
  const now = new Date();
  const diffTime = endDate.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return Math.max(0, diffDays);
});

const shouldShowBanner = computed(() => {
  if (
    !isVinctoSubscriptionPastDue.value &&
    !isVinctoSubscriptionCanceling.value &&
    !isVinctoSubscriptionTrialing.value
  )
    return false;
  if (isDismissed.value) return false;
  return true;
});

const bannerColorScheme = computed(() => {
  if (isVinctoSubscriptionTrialing.value) {
    const days = daysUntilTrialEnd.value;
    if (days !== null && days <= 3) return 'warning';
    return 'primary';
  }
  return 'warning';
});

const bannerMessage = computed(() => {
  if (isVinctoSubscriptionTrialing.value) {
    const days = daysUntilTrialEnd.value;
    if (isSuperAdmin.value) {
      if (days === 0) {
        return t('VINCTO.SUBSCRIPTION_TRIALING.SUPERADMIN_MESSAGE_LAST_DAY');
      }
      if (days !== null && days <= 7) {
        return t('VINCTO.SUBSCRIPTION_TRIALING.SUPERADMIN_MESSAGE_DAYS', {
          days,
        });
      }
      return t('VINCTO.SUBSCRIPTION_TRIALING.SUPERADMIN_MESSAGE', {
        date: formattedPeriodEnd.value,
      });
    }
    if (days === 0) {
      return t('VINCTO.SUBSCRIPTION_TRIALING.USER_MESSAGE_LAST_DAY');
    }
    if (days !== null && days <= 7) {
      return t('VINCTO.SUBSCRIPTION_TRIALING.USER_MESSAGE_DAYS', { days });
    }
    return t('VINCTO.SUBSCRIPTION_TRIALING.USER_MESSAGE', {
      date: formattedPeriodEnd.value,
    });
  }
  if (isVinctoSubscriptionCanceling.value) {
    const key = isSuperAdmin.value
      ? 'VINCTO.SUBSCRIPTION_CANCELING.SUPERADMIN_MESSAGE'
      : 'VINCTO.SUBSCRIPTION_CANCELING.USER_MESSAGE';
    return t(key, { date: formattedPeriodEnd.value });
  }
  const key = isSuperAdmin.value
    ? 'VINCTO.SUBSCRIPTION_PAST_DUE.SUPERADMIN_MESSAGE'
    : 'VINCTO.SUBSCRIPTION_PAST_DUE.USER_MESSAGE';
  return t(key);
});

const actionButtonLabel = computed(() => {
  if (isVinctoSubscriptionTrialing.value) {
    return t('VINCTO.SUBSCRIPTION_TRIALING.UPGRADE');
  }
  if (isVinctoSubscriptionCanceling.value) {
    return t('VINCTO.SUBSCRIPTION_CANCELING.OPEN_BILLING');
  }
  return t('VINCTO.SUBSCRIPTION_PAST_DUE.OPEN_BILLING');
});

function checkDismissedState() {
  const dismissedAt = LocalStorage.get(
    LOCAL_STORAGE_KEYS.VINCTO_BANNER_DISMISSED_AT
  );
  if (dismissedAt) {
    const dismissedTime = new Date(dismissedAt).getTime();
    const now = Date.now();
    if (now - dismissedTime < DISMISS_DURATION_MS) {
      isDismissed.value = true;
    } else {
      LocalStorage.remove(LOCAL_STORAGE_KEYS.VINCTO_BANNER_DISMISSED_AT);
    }
  }
}

function onDismiss() {
  isDismissed.value = true;
  LocalStorage.set(
    LOCAL_STORAGE_KEYS.VINCTO_BANNER_DISMISSED_AT,
    new Date().toISOString()
  );
}

function openSubscriptionSettings() {
  if (isSuperAdmin.value && !currentAccount.value.vincto_subscription) {
    window.location.href = '/super_admin/settings';
    return;
  }
  router.push({
    name: 'vincto_billing_index',
    params: { accountId: accountId.value },
  });
}

onMounted(() => {
  checkDismissedState();
});
</script>

<template>
  <Banner
    v-if="shouldShowBanner"
    :color-scheme="bannerColorScheme"
    :banner-message="bannerMessage"
    :notice-message="
      isSuperAdmin ? t('GENERAL_SETTINGS.SUPER_ADMIN_ONLY_NOTICE') : ''
    "
    :action-button-label="showActionButton ? actionButtonLabel : ''"
    :has-action-button="showActionButton"
    has-close-button
    @primary-action="openSubscriptionSettings"
    @close="onDismiss"
  />
</template>
